import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

interface MapboxPickerProps {
  apiKey: string;
  onLocationSelect: (location: MapLocation) => void;
  initialLocation?: Coordinates;
  initialAddress?: Address;
}

interface MapLocation {
  coordinates: Coordinates;
  address?: Address;
}

interface Coordinates {
  latitude: number;
  longitude: number;
}

interface Address {
  street: string;
  ward: string;
  city: string;
  state: string;
  country: string;
  postalCode?: string;
}

const MapboxPicker: React.FC<MapboxPickerProps> = ({
  apiKey,
  onLocationSelect,
  initialLocation,
  initialAddress
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);

  useEffect(() => {
    const initializeMap = async () => {
      if (!mapContainer.current) return;

      try {
        mapboxgl.accessToken = apiKey;
        const defaultLocation = initialLocation || { latitude: 10.762622, longitude: 106.660172 }; // Ho Chi Minh City

        const mapInstance = new mapboxgl.Map({
          container: mapContainer.current,
          style: 'mapbox://styles/mapbox/streets-v11',
          center: [defaultLocation.longitude, defaultLocation.latitude],
          zoom: 15
        });

        map.current = mapInstance;

        const markerInstance = new mapboxgl.Marker({ draggable: true })
          .setLngLat([defaultLocation.longitude, defaultLocation.latitude])
          .addTo(mapInstance);

        marker.current = markerInstance;

        // Add click event
        mapInstance.on('click', (e) => {
          const { lng, lat } = e.lngLat;
          markerInstance.setLngLat([lng, lat]);
          handleLocationSelect(lat, lng);
        });

        // Add drag event
        markerInstance.on('dragend', () => {
          const { lng, lat } = markerInstance.getLngLat();
          handleLocationSelect(lat, lng);
        });
      } catch (error) {
        console.error('Error initializing Mapbox:', error);
      }
    };

    initializeMap();

    return () => {
      if (map.current) {
        map.current.remove();
      }
      map.current = null;
      marker.current = null;
    };
  }, [apiKey, initialLocation]);

  const handleLocationSelect = async (lat: number, lng: number) => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${apiKey}`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const address = parseMapboxAddress(data.features);
        onLocationSelect({
          coordinates: { latitude: lat, longitude: lng },
          address
        });
      }
    } catch (error) {
      console.error('Error geocoding location:', error);
    }
  };

  const parseMapboxAddress = (features: any[]): Address => {
    let address: Address = {
      street: '',
      ward: '',
      city: '',
      state: '',
      country: 'Vietnam',
      postalCode: ''
    };

    features.forEach(feature => {
      const type = feature.place_type[0];
      switch (type) {
        case 'address':
          address.street = feature.text;
          break;
        case 'district':
          if (!address.ward) address.ward = feature.text;
          break;
        case 'place':
          address.city = feature.text;
          break;
        case 'region':
          address.state = feature.text;
          break;
        case 'country':
          address.country = feature.text;
          break;
        case 'postcode':
          address.postalCode = feature.text;
          break;
      }
    });

    return address;
  };

  return (
    <div className="h-96">
      <div ref={mapContainer} className="h-full w-full rounded-lg" />
    </div>
  );
};

export default MapboxPicker; 